/**
 * EntityPage - Display page for commune/EPCI entities
 * Modern version with Vite + React 19 + Material-UI v7 + TanStack Query
 * Migration from front/src/templates/EntityPage.js
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import {
  Box,
  CssBaseline,
  Dialog,
  Drawer,
  GlobalStyles,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import Layout from '../components/Layout';
import ContentPageNavigation from '../components/ContentPageNavigation';
import Search from '../components/Search';
import SpeedDial from '../components/SpeedDial';
import OutlookSwitcher from '../components/OutlookSwitcher';
import EntityPageContent from '../components/EntityPageContent';
import PrintButton from '../components/PrintButton';
import { FirstPage, LastPage } from '../components/PrintComponents';

import useEntity from '../hooks/data/useEntity';
import useEntityOutlooks from '../hooks/data/useEntityOutlooks';
import useInteractiveIframe from '../hooks/ui/useInteractiveIframe';
import useNavigationState from '../hooks/ui/useNavigationState';
import { contentHierarchy, printLastPage } from '../config/settings.js';
import { getIndicatorsWithStatus } from '../utils/dataHelpers.js';
import {
  setItemsStates,
  resetNavigationState
} from '../store/slices/navigationSlice.js';

// SSR-compatible iframe detection
const getInFrame = () => {
  if (typeof globalThis === 'undefined') return false;
  return globalThis.parent !== globalThis;
};

/**
 * Utility function for smooth scrolling to an element
 * @param {string} key - ID of the target element
 */
const scrollToKey = (key) => {
  const element = document.querySelector(`#${key}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

/**
 * EntityPage Component
 * Display page for commune and EPCI entities with climate indicators
 * @returns {JSX.Element} Entity page component
 */
const EntityPage = () => {
  const { insee } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();

  // Navigation state via custom hook (only need indicatorsWithStatus for filtering)
  const { indicatorsWithStatus } = useNavigationState();

  // Data hooks
  const { data: entity, isLoading, error } = useEntity(insee);

  // Synchronize outlooks with entity-specific horizons (for overseas territories)
  useEntityOutlooks(entity);

  // Responsive breakpoints
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.only('md'));

  let screenType = 'largeScreen';
  if (isMediumScreen) screenType = 'mediumScreen';
  if (isSmallScreen) screenType = 'smallScreen';

  // Local UI state (non-navigation related)
  const [openTableOfContentDialog, setOpenTableOfContentDialog] = useState(false);
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [openOutlookDialog, setOpenOutlookDialog] = useState(false);

  // Calculate indicators status when entity is loaded
  useEffect(() => {
    if (entity) {
      const indicators = getIndicatorsWithStatus(entity);
      dispatch(setItemsStates({
        itemsStates: indicators,
        indicatorsWithStatus: indicators,
        entityId: insee,
        entity // Pass the complete entity object
      }));
    }
  }, [entity, insee, dispatch]);

  // Reset navigation state when unmounting or changing entity
  useEffect(() => () => {
    dispatch(resetNavigationState());
  }, [dispatch, insee]);

  // Force document title update when entity changes (react-helmet workaround)
  useEffect(() => {
    if (entity) {
      const { nom, identifiant_insee, code_recherche, type: entityType } = entity;
      const displayCode = entityType === 'commune' ? code_recherche : identifiant_insee;
      const entityLabel = displayCode ? `${nom} (${displayCode})` : nom;
      document.title = entityLabel;
    }
  }, [entity]);


  // Iframe detection and communication
  const _inFrame = getInFrame();
  const { pathname = '/', search = '', hash = '' } = globalThis.location || {};

  useInteractiveIframe({
    key: `entity-${insee}`,
    location: { pathname, search, hash }
  });

  /**
   * Filter contentHierarchy based on available indicators
   * @returns {Array} Filtered content hierarchy
   */
  const getFilteredContentHierarchy = () => {
    if (!indicatorsWithStatus || Object.keys(indicatorsWithStatus).length === 0) {
      return contentHierarchy;
    }

    return contentHierarchy
      .map(group => ({
        ...group,
        items: group?.items.filter(
          ({ key, isIndicator, disabled }) => {
            if (disabled) return false;

            const hasData = key in indicatorsWithStatus;
            const notAnIndicator = !isIndicator;

            return hasData || notAnIndicator;
          },
        ),
      }))
      .filter((group, idx) => {
        // Always keep groups that had no indicator items originally (e.g. "Caractéristiques", "Comprendre")
        const originalHadIndicators = contentHierarchy[idx].items.some(({ isIndicator }) => isIndicator);
        if (!originalHadIndicators) return true;
        // For indicator groups, hide the category title if no indicator has data
        return group.items.some(({ isIndicator }) => isIndicator);
      });
  };

  /**
   * Entity selection handler from search
   * @param {Object} selectedEntity - Selected entity object
   */
  const handleEntitySelect = (selectedEntity) => {
    if (selectedEntity?.identifiant) {
      navigate(`/entity/${selectedEntity.identifiant}#caracteristiques`);
    }
  };

  // Dialog handlers
  const handleTableOfContentDialogOpen = () => setOpenTableOfContentDialog(true);
  const handleTableOfContentDialogClose = () => setOpenTableOfContentDialog(false);
  const handleSearchDialogOpen = () => setOpenSearchDialog(true);
  const handleSearchDialogClose = () => setOpenSearchDialog(false);

  // Loading and error states
  if (isLoading) {
    return (
      <Layout title="Chargement...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography variant="h6" component="p">Chargement des données...</Typography>
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Erreur">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography variant="h6" component="p" color="error">
            Erreur lors du chargement des données : {error.message}
          </Typography>
        </Box>
      </Layout>
    );
  }

  if (!entity) {
    return (
      <Layout title="Commune ou EPCI non trouvée">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography variant="h6" component="p">
            Aucune commune ou EPCI trouvée pour le code INSEE : {insee}
          </Typography>
        </Box>
      </Layout>
    );
  }

  // Build display data
  const { nom, identifiant_insee, code_recherche, type: entityType } = entity;
  // For communes, use postal code (code_recherche), for EPCI use identifiant_insee
  const displayCode = entityType === 'commune' ? code_recherche : identifiant_insee;
  const entityLabel = displayCode ? `${nom} (${displayCode})` : nom;

  const pageContentHierarchy = getFilteredContentHierarchy();

  // Reusable search component
  const searchComponent = (
    <Search
      onSelectionChange={handleEntitySelect}
    />
  );

  // Navigation component
  const navComponent = (
    <ContentPageNavigation
      pageContentHierarchy={pageContentHierarchy}
      scrollTo={scrollToKey}
      entityType={entityType}
      handleTableOfContentDialogClose={handleTableOfContentDialogClose}
      openTableOfContentDialog={openTableOfContentDialog}
      handleSearchDialogClose={handleSearchDialogClose}
      openSearchDialog={openSearchDialog}
    >
      {searchComponent}
    </ContentPageNavigation>
  );
  // const inFrame = typeof globalThis !== 'undefined' && globalThis.parent !== globalThis;

  return (
    <Layout
      title={entityLabel}
      hasHeader
      nom={nom}
    >
      <CssBaseline />
      {/* {inFrame && <CustomIframeStyle />} */}
      <GlobalStyles
        styles={{
          html: { minHeight: '100%' },
          '*': { scrollBehavior: 'smooth' },
        }}
      />

      {/* SpeedDial for mobile */}
      {isSmallScreen && (
        <SpeedDial
          code={identifiant_insee}
          nom={nom}
          handleTableOfContentDialogOpen={handleTableOfContentDialogOpen}
          handleSearchDialogOpen={handleSearchDialogOpen}
          setOpenOutlookDialog={setOpenOutlookDialog}
        />
      )}

      {/* OutlookSwitcher mobile dialog */}
      <Dialog
        disableRestoreFocus
        fullWidth
        onClose={() => setOpenOutlookDialog(false)}
        open={openOutlookDialog}
      >
        <OutlookSwitcher
          direction="column"
          onChange={() => {
            setTimeout(() => setOpenOutlookDialog(false), 300);
          }}
          sx={{
            width: 120,
            mx: 'auto',
            my: 5,
            fontSize: '1.5em',
          }}
          spacing={2}
        />
      </Dialog>

      {/* Main layout */}
      <Box
        sx={{
          display: 'flex',
          mt: '50px',
          mb: '56px',
          '@media print': { mb: 0 },
        }}
      >
        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: isSmallScreen ? 2 : 3,
            '@media print': { pb: 0 },
          }}
        >
          <FirstPage nom={nom} />
          <EntityPageContent
            entity={entity}
            contentHierarchy={pageContentHierarchy}
            screenType={screenType}
          />
          <LastPage items={printLastPage} />
        </Box>

        {/* Mobile navigation (bottom drawer) */}
        {isSmallScreen && navComponent}

        {/* Desktop navigation (sidebar) */}
        {!isSmallScreen && (
          <Drawer
            className="hidden-print"
            sx={{
              p: 1,
              width: 350,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: 350,
                mt: '50px',
                height: 'calc(100% - 50px)',
                boxSizing: 'border-box',
              },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
            }}
            variant="permanent"
            anchor="right"
          >
            <Box
              sx={{
                flexShrink: 1,
                height: '1px',
                overflow: 'hidden',
              }}
              className="sidebar-spacer"
            />

            <Box
              sx={{
                px: 3,
                overflow: 'auto',
                flexShrink: 0,
              }}
              className="sidebar-content"
            >
              <Box sx={{
                position: 'fixed',
                width: 300,
                backgroundColor: 'white',
                zIndex: 3,
                paddingTop: 3
              }}>
                {searchComponent}
                <Typography variant="h6" align="center" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                  {entityLabel}
                </Typography>
                <Divider sx={{ mt: 2, mb: 2 }} />
                <Typography variant="body2" align="center" sx={{ mt: 1.5 }}>
                  Sélectionnez votre horizon
                </Typography>
                <OutlookSwitcher sx={{ width: 240, mx: 'auto', mt: 1.5 }} />

                <PrintButton code={identifiant_insee} nom={nom} />

                <Divider light variant="middle" sx={{ mt: 2, mb: 2 }} />
              </Box>

              <Box mt={47}>{navComponent}</Box>
            </Box>
          </Drawer>
        )}
      </Box>
    </Layout>
  );
};

export default EntityPage;
